import { useState } from "react";
import { customEvent, FUNERAL_SIDO, FUNERAL_SIGUNGU } from "goi_common";
import { ParlorDetailProps } from "@/types/parlor";

import { ParlorHeader } from "./components/ParlorHeader";
import { ParlorFixedBar } from "./components/ParlorFixedBar";
import { ParlorBreadcrumb } from "./components/ParlorBreadcrumb";
import { ParlorHero } from "./components/ParlorHero";
import { ParlorInfoGrid } from "./components/ParlorInfoGrid";
import { ParlorPriceTable } from "./components/ParlorPriceTable";
import { ParlorServicesGrid } from "./components/ParlorServicesGrid";
import { ParlorNearby } from "./components/ParlorNearby";
import { ParlorFAQ } from "./components/ParlorFAQ";
import { ParlorSidebar } from "./components/ParlorSidebar";
import { useParlorInquiryModal } from "@/hooks/useParlorInquiryModal";
import { UrgentCallBannerSection } from "@/components/UrgentCallBannerExperiment";

interface NearbyParlor {
  id: number;
  companyname: string;
  sido: string;
}

interface ParlorDetailContainerProps {
  defaultParlor: ParlorDetailProps;
  nearbyParlors: NearbyParlor[];
}

export const ParlorDetailContainer = ({
  defaultParlor,
  nearbyParlors,
}: ParlorDetailContainerProps) => {
  const { openParlorInquiryModal } = useParlorInquiryModal("prlr_fam_main");
  // 헤더 전화 띠지는 첫 화면 임종 배너가 스크롤로 사라진 뒤에만 띄운다(2026-10-14 영민).
  // 첫 화면에서 둘이 같이 보이면 전화 버튼이 겹친다
  const [isBannerScrolledPast, setIsBannerScrolledPast] = useState(false);
  const {
    id,
    companyname,
    address,
    telephone,
    managed_type,
    binsos,
    services,
    expected_charge_sum,
    sido,
    sigungu,
    hospital_link,
    traffpublic,
    binso_average_charge,
    images,
  } = defaultParlor;

  const sidoName = FUNERAL_SIDO.get(Number(sido)) ?? sido ?? "";
  const sigunguName =
    FUNERAL_SIGUNGU.get(Number(sido))?.get(Number(sigungu)) ?? sigungu ?? "";
  const binsoCount = binsos.length;
  const coverImages = images.slice(-3).reverse();

  return (
    <div className="pd-page">
      <ParlorHeader
        hidePhoneStrip={!isBannerScrolledPast}
        floatPhoneStrip
        phoneStripLabel="임종 접수 · 24시간"
        onPhoneStripClick={() => {
          customEvent({
            newGtm: {
              path: "prlr_fam_main",
              trigger_and_target: "hdr_consult",
            },
          });
          void openParlorInquiryModal();
        }}
      />
      {/* now 콜 확대 임종 배너. 배너가 사라지면 하단 버튼 대신 위 헤더 띠지가 나온다 */}
      <UrgentCallBannerSection
        page="prlr_fam_main"
        parlor={{ id: Number(id), name: companyname }}
        hideFloating
        onScrolledPastChange={setIsBannerScrolledPast}
        isLive
      />
      <ParlorBreadcrumb
        sidoName={sidoName}
        sigunguName={sigunguName}
        companyName={companyname}
      />

      {coverImages.length > 0 && (
        <div className="pd-cover-wrap">
          <div
            className={`pd-cover pd-cover--${Math.min(coverImages.length, 3)}`}
          >
            {coverImages.map((img, i) => (
              <img
                key={img.url}
                src={img.url}
                alt={`${companyname} 장례식장 시설 사진 ${i + 1}`}
                className="pd-cover__img"
                loading={i === 0 ? "eager" : "lazy"}
              />
            ))}
          </div>
        </div>
      )}

      <ParlorHero
        companyName={companyname}
        sidoName={sidoName}
        sigunguName={sigunguName}
        managedType={managed_type}
        binsoCount={binsoCount}
        address={address}
        parlorId={id}
      />

      <div className="pd-main">
        <div className="pd-content">
          <ParlorInfoGrid
            parlor={defaultParlor}
            companyName={companyname}
            address={address}
            telephone={telephone}
            managedType={managed_type}
            binsoCount={binsoCount}
          />

          <ParlorPriceTable
            companyName={companyname}
            binsos={binsos}
            services={services}
          />

          {/*
            무빈소 페이지로 보내는 다리. 요금표 안에 두면 binsos 가 없는
            장례식장(12곳)에서 ParlorPriceTable 이 null 을 반환해 링크가 빠진다.
          */}
          <a
            className="pd-price-crosslink"
            href={`/place/${id}/mubinso/`}
            onClick={() =>
              customEvent({
                newGtm: {
                  path: "prlr_fam_main",
                  trigger_and_target: "btn_to_mubinso",
                },
              })
            }
          >
            {`빈소를 차리지 않으면? ${companyname} 무빈소 장례 비용 보기 →`}
          </a>

          <ParlorServicesGrid companyName={companyname} services={services} />

          <ParlorNearby
            companyName={companyname}
            hospitalLink={hospital_link}
            sidoName={sidoName}
            sigunguName={sigunguName}
            parlorId={id}
            nearbyParlors={nearbyParlors}
          />

          <ParlorFAQ
            companyName={companyname}
            telephone={telephone}
            traffpublic={traffpublic}
            binsoAverageCharge={binso_average_charge}
            sigunguName={sigunguName}
          />
        </div>

        <ParlorSidebar
          companyName={companyname}
          parlorId={id}
          expectedChargeSum={expected_charge_sum}
        />
      </div>

      <ParlorFixedBar sidoName={sidoName} sigunguName={sigunguName} />
    </div>
  );
};
